<!--
    WorkIQ "What's on your desk" (PLT-2189).

    Reuses the existing insight-card components read-only - InsightCardLayout for the gradient bar
    and frame, InsightCategoryLabel for the tier dot, DiveDeeperButton for the CTA - so this reads as
    one of the pipeline's insight cards. Reuse only: nothing under ~vue/Labs or
    coach_core/intelligence_pipeline is modified, and no new value is registered in any of their
    enums or unions.

    The one thing not reused is InsightEvidenceChips, because its analyticsContext.location is a
    closed union in Labs/evidence.ts that would have to gain a WorkIQ value. The chips are rendered
    here instead against a WorkIQ-owned CitationKind, which costs the popovers and the overflow
    measurement but keeps the pipeline untouched. Provenance is stated outright with a "Powered by
    Work IQ" chip.
-->
<template>
    <section v-if="items.length" data-testid="home-mywork-insights-card">
        <HomeSectionHeader title="What's on your desk">
            <template #actions>
                <span class="inline-flex shrink-0 items-center gap-2 details text-ink-muted">
                    <span class="hidden whitespace-nowrap sm:inline">{{ insights.period_label }}</span>
                    <SourceChip class="text-ink-muted">Powered by Work IQ</SourceChip>
                    <span v-if="items.length > 1" class="whitespace-nowrap">{{ index + 1 }} of {{ items.length }}</span>
                </span>
            </template>
        </HomeSectionHeader>

        <BaseCarousel
            v-model="activeInsightId"
            :item-ids="items.map((item) => item.insight_id)"
            :label="$t('home.workIq.carouselLabel')"
            :item-label="$t('home.insights.itemLabel')"
            fit-active>
            <template v-for="item in items" :key="item.insight_id" #[`item-${item.insight_id}`]>
                <InsightCardLayout v-if="item.insight_id === activeInsightId">
                    <Transition mode="out-in" name="fade">
                        <div :key="`${current.insight_id}-meta`" class="mb-3 flex flex-wrap items-center gap-2">
                            <InsightCategoryLabel :label="categoryLabel" :tier="WORKIQ_TIER" />
                            <span v-if="current.freshness === 'recent'" class="scaffold-details-small text-ink-muted">• Already happened</span>
                            <span v-if="current.confidence === 'low'" class="scaffold-details-small text-ink-muted">• Worth checking</span>
                        </div>
                    </Transition>

                    <!-- min-heights keep short cards aligned while letting a longer title or body expand the
                 card to fit, matching the pipeline's insight card. -->
                    <Transition mode="out-in" name="fade">
                        <div :key="current.title" class="mb-3 flex min-h-14 items-center">
                            <h4 class="display-small-serif text-ink">
                                <MarkdownText :text="current.title" inline />
                            </h4>
                        </div>
                    </Transition>

                    <Transition mode="out-in" name="fade">
                        <div :key="current.body">
                            <MarkdownText
                                :text="current.body"
                                class="min-h-15 body-small prose prose-sm prose-slate max-w-none text-ink-muted prose-headings:my-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-li:leading-5" />
                            <p v-if="current.unknowns.length" class="mt-2 scaffold-details-small text-ink-muted">Not known from the evidence: {{ current.unknowns.join("; ") }}</p>
                        </div>
                    </Transition>

                    <HorizontalDivider as="hr" spacing="none" class="mt-4" />

                    <Transition mode="out-in" name="fade">
                        <div :key="`${current.insight_id}-citations`" class="mt-3 flex items-center justify-between gap-3">
                            <div v-if="citations.length" class="flex min-w-0 flex-wrap items-center gap-2">
                                <span class="shrink-0 scaffold-details-small text-ink-muted">Based on:</span>
                                <a
                                    v-for="citation in citations"
                                    :key="citation.url"
                                    :href="citation.url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :title="citation.detail || citation.label"
                                    class="inline-flex shrink-0 items-center gap-2 rounded-full bg-card-surface-raised px-3 py-1 scaffold-details-small text-ink transition-colors hover:bg-surface-hover focus-ring">
                                    <i :class="citation.icon" class="text-[11px]/none" aria-hidden="true"></i>
                                    <span>{{ citation.label }}</span>
                                </a>
                            </div>
                            <span v-else class="scaffold-details-small text-ink-muted">No linkable sources on this one.</span>

                            <DiveDeeperButton data-testid="home-mywork-insights-start" :loading="starting" @click="startChat" />
                        </div>
                    </Transition>
                    <span v-if="error" role="alert" class="details mt-1 text-status-error">{{ error }}</span>
                </InsightCardLayout>
            </template>
        </BaseCarousel>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import HomeSectionHeader from "~vue/ChatHome/HomeSectionHeader.vue";
import BaseCarousel from "~vue/components/BaseCarousel.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import DiveDeeperButton from "~vue/Labs/DiveDeeperButton.vue";
import type { InsightTier } from "~vue/Labs/insightCategory";
import InsightCategoryLabel from "~vue/Labs/InsightCategoryLabel.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import type { MyWorkCitationKind, MyWorkInsightsCardData } from "~vue/types/chat_home";
import { openUrl } from "~vue/utils";
import { computed, ref } from "vue";

const props = withDefaults(
    defineProps<{
        insights: MyWorkInsightsCardData;
        startUrl?: string;
    }>(),
    { startUrl: "/workiq/api/insights-chat/" },
);

// Tier 4 is already "Work IQ" in the existing tier system (see insightCategory.ts), so the dot
// colour comes from it with nothing registered and nothing changed.
const WORKIQ_TIER: InsightTier = 4;

// WorkIQ-owned icon map, keyed by CitationKind. Kept here rather than imported so the card does not
// depend on the insights pipeline's evidence-icon registry.
const CITATION_ICONS: Record<MyWorkCitationKind, string> = {
    teams: "bi bi-people-fill",
    outlook: "bi bi-envelope",
    calendar: "bi bi-calendar-event",
    documents: "bi bi-file-earmark-text",
    microsoft_365: "bi bi-microsoft",
};

const CATEGORY_LABELS: Record<string, string> = {
    meeting_prep: "Meeting prep",
    calendar_change: "Calendar change",
    open_loop: "Open loop",
    relationship: "Relationship",
    goal_progress: "Goal progress",
    workload: "Workload",
};

const index = ref(0);
const starting = ref(false);
const error = ref<string | null>(null);

const items = computed(() => props.insights.items);
const current = computed(() => items.value[Math.min(index.value, items.value.length - 1)]);
const activeInsightId = computed({
    get: () => current.value?.insight_id ?? "",
    set: (id: string) => goTo(items.value.findIndex((item) => item.insight_id === id)),
});

// Flattened to one chip per citation so every chip is a real link. Grouping survives only as the
// icon: the server groups by surface, and the kind is what picks the glyph.
const citations = computed(() =>
    (current.value.citation_groups ?? []).flatMap((group) =>
        (group.citations ?? []).map((citation) => ({ ...citation, icon: CITATION_ICONS[group.kind] ?? CITATION_ICONS.microsoft_365 })),
    ),
);

// An unmapped category is LLM output we have not seen before; humanise the raw value rather than
// dropping the line.
// `??` would not help here: the server sends "" rather than null for a missing category, and
// "".replace() is still "", so the fallback has to be `||`.
const categoryLabel = computed(() => CATEGORY_LABELS[current.value.category] || current.value.category.replace(/_/g, " ") || "Insight");

function goTo(next: number) {
    index.value = Math.min(Math.max(next, 0), items.value.length - 1);
    // The error belongs to the insight it was raised on, and there is no dismiss affordance.
    error.value = null;
}

async function startChat() {
    if (starting.value) {
        return;
    }
    starting.value = true;
    error.value = null;
    try {
        const { data, response } = await useFetch(props.startUrl).post({}).json<{ coaching_session_url?: string; error?: string }>();
        if (response.value?.ok && data.value?.coaching_session_url) {
            openUrl(data.value.coaching_session_url);
        } else {
            error.value = data.value?.error || "Something went wrong. Please try again.";
        }
    } finally {
        starting.value = false;
    }
}
</script>
